// Product flow (Mitch: "a flow chart that we can click/pan around … the finished made product is at the bottom, and
// flowing up you can see each component that makes up each step, along with bubbles representing the locations of where
// a step happens"), a project's Product flow tab (Mitch, 2026-11-01: "makes more sense to only have in the projects …
// to visualize the products contained in a project"; /flow redirects to Projects): pick one of the project's products
// (and its variant) or a sub-assembly nothing else uses, the first shown to start; shared/flow.ts builds the tree from
// its current BOM and places the boxes. Each box has its photo, SKU, quantity per finished unit and one tag, in the same
// place on every box: made in-house ("Elecrow makes", purple), ordered from elsewhere ("Elecrow orders", amber), or
// bought by HHL ("HHL sends" to the CM, "HHL buys" for its own builds; teal); many loose parts sit in one block. Drag
// to pan, the wheel or the buttons to zoom, Fit to see it all (zoomed out, a box shows just its name on its tag's
// colour); a box opens its part in the side pane (Ctrl/⌘-click: its page). `?item=` the product's variant or the
// sub-assembly; `?build=` one of its builds, whose state shows on the boxes (BuildStatus.tsx).
import { Maximize, Minus, Plus } from "lucide-react";
import { type CSSProperties, type PointerEvent as ReactPointerEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { type BomItem, bomOf, families, variantsOf } from "../../../../shared/boms.ts";
import { FLOW_SIZE, type FlowLayout, type FlowWho, flowLines, layoutFlow, type PlacedNode, productFlow, qtyText, whoText } from "../../../../shared/flow.ts";
import type { MakeDashboard, Readiness, ReadinessLine } from "../../../../shared/make.ts";
import type { Plan } from "../../../../shared/planning.ts";
import { useBomStructure } from "../../lib/boms.ts";
import { setSearch, useSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Button, cx, Select, Thumb } from "../../ui/controls.tsx";
import { usePaneOpen } from "../../ui/SidePane.tsx";
import { BuildCard, BuiltBar, buildLabel, MakeLoader, PartBar, partText, partTitle } from "./BuildStatus.tsx";

/** Each tag's colours: made in-house purple (the accent), ordered amber, bought by HHL teal. */
const TAG: Record<FlowWho, string> = {
  made: "bg-accent-soft text-accent-text border-accent/30",
  ordered: "bg-warn-soft text-warn border-warn/30",
  sent: "bg-[color-mix(in_srgb,var(--series-3)_14%,transparent)] text-[var(--series-3)] border-[color-mix(in_srgb,var(--series-3)_35%,transparent)]",
  bought: "bg-[color-mix(in_srgb,var(--series-3)_14%,transparent)] text-[var(--series-3)] border-[color-mix(in_srgb,var(--series-3)_35%,transparent)]",
};
const LEGEND: { who: FlowWho; text: string }[] = [
  { who: "made", text: "Made in-house there" },
  { who: "ordered", text: "Ordered from elsewhere by the maker" },
  { who: "sent", text: "Bought by HHL (and sent to the maker)" },
];

/** Something the flow can show: a product with a BOM (with its variants) or a sub-assembly with one. */
export type FlowRoot = { value: string; label: string; variants: BomItem[] };

/** What can be shown (those `only` keeps), by name. */
function useRoots(only: (root: FlowRoot) => boolean) {
  const ix = useBomStructure();
  return useMemo(() => {
    const products: FlowRoot[] = [];
    for (const b of families(ix)) {
      const variants = variantsOf(ix, b).filter((v) => !v.archived);
      if (!variants.length) continue;
      const first = variants[0];
      const label = b.shopify_product_id ? first.shopify_product_title || first.name : `${first.sku} · ${first.name}`;
      const root = { value: b.shopify_product_id ? `product:${b.shopify_product_id}` : first.id, label, variants };
      if (only(root)) products.push(root);
    }
    return { ix, products: products.sort((a, b) => a.label.localeCompare(b.label)) };
  }, [ix, only]);
}

/** The flow of one of `only`'s products or sub-assemblies (`?item=`, else the first). */
export function ProductFlow({ only }: { only: (root: FlowRoot) => boolean }) {
  const params = useSearch();
  const { ix, products } = useRoots(only);
  const itemId = params.get("item") ?? products[0]?.variants[0]?.id ?? "";
  const item = ix.itemById.get(itemId);
  const product = products.find((p) => p.variants.some((v) => v.id === itemId));
  const bom = item ? bomOf(ix, item) : undefined;

  const vendors = useRows("vendor");
  const links = useRows("vendor_item");
  const items = useRows("item");
  const photo = useMemo(() => new Map(items.map((i) => [i.id, i.photo_url])), [items]);
  const names = useMemo(() => {
    const vendorName = new Map(vendors.map((v) => [v.id, v.short_name || v.name]));
    // An item's supplier: its preferred link's vendor, else its only one.
    const supplier = new Map<string, string>();
    for (const l of [...links].sort((a, b) => Number(a.is_preferred) - Number(b.is_preferred)))
      if (vendorName.has(l.vendor_id)) supplier.set(l.item_id, vendorName.get(l.vendor_id) as string);
    return { vendor: (id: string) => vendorName.get(id) ?? "?", supplier: (id: string) => supplier.get(id) ?? null };
  }, [vendors, links]);
  const layout = useMemo(() => (bom && item ? layoutFlow(productFlow(ix, names, bom, item)) : null), [ix, names, bom, item]);
  const present = useMemo(() => new Set(layout?.nodes.map((p) => p.node.who)), [layout]);

  // Its builds come from the Make plan, worked out after the first paint (the first plan takes about 0.5 s).
  const [make, setMake] = useState<{ plan: Plan; dash: MakeDashboard } | null>(null);
  const [later, setLater] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setLater(true), 0);
    return () => clearTimeout(id);
  }, []);
  const builds = useMemo(() => (make ? make.dash.cms.flatMap((c) => c.builds).filter((rd) => rd.build.itemId === itemId) : []), [make, itemId]);
  const picked = builds.find((rd) => rd.build.key === params.get("build")) ?? null;
  const build = useMemo((): FlowBuild | null => {
    if (!make || !picked || !layout) return null;
    const lines = flowLines(layout.root.node, picked.lines, (id) => make.dash.byItem.get(id)?.readiness?.lines);
    return { rd: picked, today: make.plan.today, lines, site: make.dash.byItem.get(itemId)?.site ?? null };
  }, [make, picked, layout, itemId]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2">
        <div className="w-72">
          <Select
            aria-label="Product"
            value={product?.value ?? ""}
            options={products.map((p) => ({ value: p.value, label: p.label }))}
            onChange={(e) => setSearch({ item: products.find((p) => p.value === e.target.value)?.variants[0].id ?? null, build: null })}
            className="h-8"
          />
        </div>
        {product && product.variants.length > 1 && (
          <div className="w-56">
            <Select
              aria-label="Variant"
              value={itemId}
              options={product.variants.map((v) => ({ value: v.id, label: v.shopify_variant_title || v.sku }))}
              onChange={(e) => setSearch({ item: e.target.value, build: null })}
              className="h-8"
            />
          </div>
        )}
        {later && <MakeLoader onLoad={setMake} />}
        {builds.length > 0 && (
          <div className="w-64">
            <Select
              aria-label="Build"
              value={picked?.build.key ?? ""}
              options={[{ value: "", label: "No build: just the parts" }, ...builds.map((rd) => ({ value: rd.build.key, label: buildLabel(rd) }))]}
              onChange={(e) => setSearch({ build: e.target.value || null })}
              className="h-8"
            />
          </div>
        )}
        {layout && (
          <div className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted">
            {LEGEND.filter((l) => present.has(l.who) || (l.who === "sent" && present.has("bought"))).map((l) => (
              <span key={l.who} className="flex items-center gap-1.5">
                <span className={cx("size-3 rounded-sm border", TAG[l.who])} />
                {l.text}
              </span>
            ))}
          </div>
        )}
      </div>
      {!item ? (
        <div className="m-auto max-w-sm p-6 text-center text-muted">
          {products.length
            ? "Pick a product to see how it's made: every part from where it comes from, up top, down to the finished product."
            : "No product in this project has a BOM yet. Add its products and their BOMs, and how each is made shows here."}
        </div>
      ) : !bom || !layout ? (
        <div className="m-auto p-6 text-muted">{item.sku || item.name} has no BOM yet.</div>
      ) : (
        <FlowCanvas key={item.id} layout={layout} photo={photo} sell={item.kind === "sell"} build={build} />
      )}
    </div>
  );
}

/** A build shown on the boxes: its readiness, each box's line and where it's built, and the CM's site (to ship to). */
type FlowBuild = { rd: Readiness; today: string; lines: Map<string, { line: ReadinessLine; at: string }>; site: string | null };

type View = { x: number; y: number; k: number };
const PAD = 40;
/** Below the product: where finished units go (a product's) as a pill under it. */
const DEST_GAP = 34;
const DEST_H = 30;
/** Zoomed out past this, a box shows only its name, larger, on its tag's colour, so a whole product still reads. */
const FAR = 0.6;

/** The boxes on a canvas that pans (drag) and zooms (the wheel, about the pointer; the buttons; Fit). */
function FlowCanvas({ layout, photo, sell, build }: { layout: FlowLayout; photo: Map<string, string>; sell: boolean; build: FlowBuild | null }) {
  const open = usePaneOpen();
  const box = useRef<HTMLDivElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>({ x: 0, y: 0, k: 1 });
  const drag = useRef<{ x: number; y: number; vx: number; vy: number; moved: boolean } | null>(null);
  const height = layout.height + (sell ? DEST_GAP + DEST_H : 0);
  const root = layout.root;
  const far = view.k < FAR;
  const shownBuild = build?.rd.build.key;

  const fit = useCallback(() => {
    const el = box.current;
    if (!el) return;
    // A build's card takes its height off the bottom (the top on a phone), so it never covers the product.
    const c = card.current;
    const reserve = c ? c.offsetHeight + 12 : 0;
    const k = Math.min(1, (el.clientWidth - 2 * PAD) / layout.width, (el.clientHeight - 2 * PAD - reserve) / height);
    const kk = Math.max(0.15, k);
    const y = Math.max(PAD, (el.clientHeight - reserve - height * kk) / 2) + (c && c.offsetTop < el.clientHeight / 2 ? reserve : 0);
    setView({ k: kk, x: (el.clientWidth - layout.width * kk) / 2, y });
  }, [layout, height]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a build shown or hidden is the trigger (its card's height)
  useLayoutEffect(fit, [fit, shownBuild]);

  // Zoom about a point of the box (the pointer, or its middle).
  const zoom = useCallback((factor: number, at?: { x: number; y: number }) => {
    const el = box.current;
    if (!el) return;
    setView((v) => {
      const k = Math.min(2.5, Math.max(0.15, v.k * factor));
      const p = at ?? { x: el.clientWidth / 2, y: el.clientHeight / 2 };
      return { k, x: p.x - ((p.x - v.x) * k) / v.k, y: p.y - ((p.y - v.y) * k) / v.k };
    });
  }, []);
  // The wheel zooms (it isn't passive, so the page doesn't scroll instead).
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoom(Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015)), { x: e.clientX - r.left, y: e.clientY - r.top });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoom]);

  const down = (e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    drag.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y, moved: false };
  };
  const move = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.moved && Math.hypot(dx, dy) < 4) return;
    if (!d.moved) box.current?.setPointerCapture(e.pointerId);
    d.moved = true;
    setView((v) => ({ ...v, x: d.vx + dx, y: d.vy + dy }));
  };
  const up = (e: ReactPointerEvent) => {
    if (box.current?.hasPointerCapture(e.pointerId)) box.current.releasePointerCapture(e.pointerId);
    // A drag that ends on a box isn't a click on it.
    setTimeout(() => {
      drag.current = null;
    });
  };

  const { w, h, gapY } = FLOW_SIZE;
  return (
    <div
      ref={box}
      className="relative min-h-0 flex-1 cursor-grab touch-none overflow-hidden bg-[radial-gradient(var(--line)_1px,transparent_1px)] [background-size:20px_20px] select-none active:cursor-grabbing"
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      onKeyDown={(e) => {
        if (e.key === "+" || e.key === "=") zoom(1.2);
        else if (e.key === "-") zoom(1 / 1.2);
        else if (e.key === "0") fit();
      }}
      tabIndex={-1}
    >
      <div
        className="absolute top-0 left-0 origin-top-left"
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})`, width: layout.width, height }}
      >
        <svg className="absolute inset-0 overflow-visible" width={layout.width} height={height} aria-hidden>
          <defs>
            <marker id="flow-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M2 1L8 5L2 9" fill="none" stroke="var(--line-strong)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </marker>
          </defs>
          {layout.edges.map(({ key, x1, x2, y }) => {
            return (
              <path
                key={key}
                d={`M${x1} ${y - gapY} V${y - gapY / 2} H${x2} V${y - 2}`}
                fill="none"
                stroke="var(--line-strong)"
                strokeWidth={1.2}
                markerEnd="url(#flow-arrow)"
              />
            );
          })}
          {sell && (
            <path
              d={`M${root.x + w / 2} ${root.y + h} V${root.y + h + DEST_GAP - 2}`}
              fill="none"
              stroke="var(--line-strong)"
              strokeWidth={1.2}
              markerEnd="url(#flow-arrow)"
            />
          )}
        </svg>
        {layout.blocks.map((b) => (
          <div key={b.key} className="absolute rounded-xl border border-line bg-surface-2/60" style={{ left: b.x, top: b.y, width: b.w, height: b.h }}>
            <div
              className="truncate px-3 font-medium text-muted"
              style={far ? { fontSize: Math.min(22, 12 / view.k), lineHeight: 1.2, paddingTop: 2 } : { fontSize: 12, paddingTop: 6 }}
            >
              {b.count} parts
            </div>
          </div>
        ))}
        {layout.nodes.map((p) => (
          <FlowBox
            key={p.node.key}
            p={p}
            photo={photo.get(p.node.item.id)}
            root={p === root}
            far={far ? view.k : null}
            build={build}
            dragged={() => !!drag.current?.moved}
            open={open}
          />
        ))}
        {sell && (
          <div
            className="absolute flex items-center justify-center rounded-full border font-medium whitespace-nowrap shadow-sm"
            style={{
              left: root.x + w / 2 - 70,
              top: root.y + h + DEST_GAP,
              width: 140,
              height: DEST_H,
              fontSize: far ? Math.min(22, 12 / view.k) : 12,
              ...TAG_STYLE_STOCK,
            }}
          >
            HHL stock
          </div>
        )}
      </div>
      {build && <BuildCard ref={card} rd={build.rd} today={build.today} place={root.node.place} site={build.site} itemId={root.node.item.id} />}
      <div className="absolute right-3 bottom-3 flex items-center gap-1 rounded-md border border-line bg-surface p-1 shadow-sm">
        <Button size="sm" variant="ghost" icon={<Minus className="size-3.5" />} onClick={() => zoom(1 / 1.25)} aria-label="Zoom out" />
        <span className="w-10 text-center text-[12px] text-muted tabular-nums">{Math.round(view.k * 100)}%</span>
        <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={() => zoom(1.25)} aria-label="Zoom in" />
        <Button size="sm" variant="ghost" icon={<Maximize className="size-3.5" />} onClick={fit} title="See it all (0)">
          Fit
        </Button>
      </div>
    </div>
  );
}

const TAG_STYLE_STOCK: CSSProperties = {
  background: "color-mix(in srgb, var(--series-3) 14%, var(--surface))",
  color: "var(--series-3)",
  borderColor: "color-mix(in srgb, var(--series-3) 35%, transparent)",
};

/**
 * One part: its photo, name, SKU and quantity per finished unit, and its tag (who makes or supplies it, from whom). Far
 * (the zoom, when zoomed out past FAR): only its name, sized to read at that zoom, on its tag's colour.
 */
function FlowBox({
  p,
  photo,
  root,
  far,
  build,
  dragged,
  open,
}: {
  p: PlacedNode;
  photo: string | undefined;
  root: boolean;
  far: number | null;
  build: FlowBuild | null;
  dragged: () => boolean;
  open: ReturnType<typeof usePaneOpen>;
}) {
  const n = p.node;
  const at = build?.lines.get(n.key);
  const mo = root ? build?.rd.build.makeOrder : null;
  const today = build?.today ?? "";
  const status = at
    ? { ...partText(at.line, today, at.at), title: partTitle(at.line, today, at.at) }
    : root && build
      ? { text: mo ? `${mo.output} of ${mo.qty} built` : "Planned", tone: mo?.output ? "text-ok" : "text-muted", title: "" }
      : null;
  const bar = far ? Math.min(12, 4 / far) : 5; // under the name's third line
  return (
    <a
      href={`${n.item.kind === "sell" ? "/items/sell" : "/parts"}?open=${n.item.id}`}
      draggable={false}
      onClick={(e) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; // its page, in a new tab
        e.preventDefault();
        if (!dragged()) open("item", n.item.id);
      }}
      className={cx(
        "absolute flex flex-col gap-1.5 rounded-lg border p-2 text-left shadow-sm transition-colors hover:border-accent",
        far ? cx(TAG[n.who], "border-2") : "bg-surface",
        !far && (root ? "border-line-strong" : "border-line"),
        root && "ring-2 ring-accent/20",
      )}
      style={{ left: p.x, top: p.y, width: FLOW_SIZE.w, height: FLOW_SIZE.h }}
      title={`${n.item.sku || n.item.name}: ${whoText(n)}${n.from ? ` from ${n.from}` : ""}${n.ownBuild ? " in a build of its own" : ""}.${status?.title ? ` ${status.title}.` : ""} Click to open it`}
    >
      {far ? (
        <div className="line-clamp-3 leading-[1.15] font-semibold" style={{ fontSize: Math.min(22, 12 / far) }}>
          {n.item.name}
        </div>
      ) : (
        <FlowBoxText n={n} photo={photo} root={root} status={status} />
      )}
      {at && <PartBar l={at.line} height={bar} />}
      {mo && <BuiltBar built={mo.output} of={mo.qty} height={bar} />}
    </a>
  );
}

function FlowBoxText({
  n,
  photo,
  root,
  status,
}: {
  n: PlacedNode["node"];
  photo: string | undefined;
  root: boolean;
  status: { text: string; tone: string } | null;
}) {
  return (
    <>
      <div className="flex min-w-0 items-start gap-2">
        <Thumb src={photo} size={36} className="rounded-md" />
        <div className="min-w-0 flex-1">
          <div className="line-clamp-2 text-[12px] leading-tight font-medium">{n.item.name}</div>
          <div className="truncate font-mono text-[11px] text-muted">{n.item.sku}</div>
        </div>
        {!root && <span className="shrink-0 text-[12px] text-muted tabular-nums">{qtyText(n.qty)}</span>}
      </div>
      <div className="mt-auto flex min-w-0 items-center gap-1.5">
        <span className={cx("shrink-0 rounded-full border px-2 py-px text-[11px] font-medium", TAG[n.who])}>{whoText(n)}</span>
        {status ? (
          <span className={cx("truncate text-[11px] font-medium", status.tone)}>{status.text}</span>
        ) : (
          <span className="truncate text-[11px] text-faint">
            {n.from ? `from ${n.from}` : n.ownBuild ? "its own build" : n.children.length ? "from the parts above" : ""}
          </span>
        )}
      </div>
    </>
  );
}
